import { useState } from "react"
import { User, MapPin, GraduationCap, Briefcase, Upload, CheckCircle } from "lucide-react";
import authService from "../../../Services/authService";

export default function PhleboloSignup() {
  const [formData, setFormData] = useState({
    fullname: "",
    dob: "",
    gender: "",
    mobile: "",
    email: "",
    adharnumber: "",
    currentaddress: "",
    city: "",
    state: "",
    pincode: "",
    qualification: "",
    specialization: "",
    institution_name: "",
    passedout: "",
    phleblot_experience: "",
    total_experience: "",
    previous_organization: "",
    languages: [],
    otherLanguage: "",
    travel: "",
    two_wheeler: "",
    license_number: "",
    declaration: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    resume: null,
    certificate: null,
    id_proof: null,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "languages") {
      const updatedLanguages = checked
        ? [...formData.languages, value]
        : formData.languages.filter((lang) => lang !== value);
      setFormData((prev) => ({ ...prev, languages: updatedLanguages }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileUpload = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles((prev) => ({ ...prev, [fileType]: file }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullname.trim()) newErrors.fullname = "Full name is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = "Enter valid 10-digit mobile number starting with 6-9";
    }
    if (!formData.email.trim()) newErrors.email = "Email address is required";
    if (!formData.currentaddress.trim()) newErrors.currentaddress = "Current address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.pincode.trim()) newErrors.pincode = "Pincode is required";
    if (!formData.qualification) newErrors.qualification = "Qualification is required";
    if (!formData.phleblot_experience) newErrors.phleblot_experience = "Experience info is required";
    //if (!formData.travel) newErrors.travel = "Travel willingness is required";
    if (!formData.sample_collection) newErrors.sample_collection = "Travel willingness is required";
    if (!formData.two_wheeler) newErrors.two_wheeler = "Two-wheeler info is required";
    if (!formData.declaration) newErrors.declaration = "You must agree to the declaration";
    if (formData.two_wheeler === "Yes" && !formData.license_number.trim()) {
      newErrors.license_number = "Driving license is required";
    }
    setErrors(newErrors);
    console.log("Validation Errors", newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file); // returns "data:[mime];base64,XXXX"
        });
      };

      const resumeBase64 = uploadedFiles.resume ? await fileToBase64(uploadedFiles.resume) : "";
      const certificateBase64 = uploadedFiles.certificate ? await fileToBase64(uploadedFiles.certificate) : "";
      const idProofBase64 = uploadedFiles.id_proof ? await fileToBase64(uploadedFiles.id_proof) : "";

      const payload = {
        ...formData,
        languages: formData.languages.join(", "),
        resume: resumeBase64,
        certificate: certificateBase64,
        id_proof: idProofBase64,
      };

      const response = await fetch(authService.getApiUrl() + "addphleblo", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        alert("Registration submitted successfully! We will contact you soon.");
        resetForm();
      } else {
        console.error(result);
        alert(result.message || "Registration failed. Please try again.");
      }
    } catch (err) {
      console.error("File conversion or submission failed:", err);
      alert("An error occurred while submitting. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullname: "",
      dob: "",
      gender: "",
      mobile: "",
      email: "",
      adharnumber: "",
      currentaddress: "",
      city: "",
      state: "",
      pincode: "",
      qualification: "",
      specialization: "",
      institution_name: "",
      passedout: "",
      phleblot_experience: "",
      total_experience: "",
      previous_organization: "",
      languages: [],
      otherLanguage: "",
      travel: "",
      two_wheeler: "",
      license_number: "",
      declaration: false,
    });

    setUploadedFiles({
      resume: null,
      certificate: null,
      id_proof: null,
    });

    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-[#951C25] mb-2 ">Phlebotomist Registration</h1>
          <p className="text-gray-600">Join our team of healthcare professionals and help us serve patients better</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <User className="mr-2 text-[#951C25]" size={20} />
              Personal Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="md:col-span-2 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.fullname ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter your full name"
                />
                {errors.fullname && <p className="mt-1 text-xs text-red-500">{errors.fullname}</p>}
              </div>

              {/* Date of Birth */}
              <div className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.dob ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                />
                {errors.dob && <p className="mt-1 text-xs text-red-500">{errors.dob}</p>}
              </div>

              {/* Gender */}
              <div className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mt-2">
                  {["Male", "Female", "Other"].map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value={option}
                        checked={formData.gender === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.gender && <p className="mt-1 text-xs text-red-500">{errors.gender}</p>}
              </div>

              {/* Mobile Number */}
              <div className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  pattern="[6-9]{1}[0-9]{9}"
                  maxLength="10"
                  className={`w-full px-3 py-2 border ${errors.mobile ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter 10-digit mobile number"
                />
                {errors.mobile && <p className="mt-1 text-xs text-red-500">{errors.mobile}</p>}
              </div>

              {/* Email Address */}
              <div className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter your email address"
                />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>

              {/* Aadhaar Number */}
              <div className="md:col-span-2 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Number (Optional)</label>
                <input
                  type="text"
                  name="adharnumber"
                  value={formData.adharnumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                  placeholder="Enter 12-digit Aadhaar number"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-lg shadow-sm p-6 text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <MapPin className="mr-2 text-[#951C25]" size={20} />
              Address
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Current Address */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="currentaddress"
                  value={formData.currentaddress}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full px-3 py-2 border ${errors.currentaddress ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter your complete address"
                />
                {errors.currentaddress && <p className="mt-1 text-xs text-red-500">{errors.currentaddress}</p>}
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.city ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter city"
                />
                {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.state ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter state"
                />
                {errors.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.pincode ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                  placeholder="Enter 6-digit pincode"
                />
                {errors.pincode && <p className="mt-1 text-xs text-red-500">{errors.pincode}</p>}
              </div>
            </div>
          </div>

          {/* Educational Qualification */}
          <div className="bg-white rounded-lg shadow-sm p-6 text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <GraduationCap className="mr-2 text-[#951C25]" size={20} />
              Educational Qualification
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Highest Qualification */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Highest Qualification <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-4 mt-2">
                  {["Diploma", "B.Sc", "M.Sc"].map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name="qualification"
                        value={option}
                        checked={formData.qualification === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="qualification"
                      value="Other"
                      checked={formData.qualification === "Other"}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Other:</span>
                    <input
                      type="text"
                      name="otherQualification"
                      value={formData.otherQualification}
                      onChange={handleInputChange}
                      className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#951C25]/20"
                      placeholder="Specify"
                      disabled={formData.qualification !== "Other"}
                    />
                  </label>
                </div>
                {errors.qualification && (
                  <p className="mt-1 text-xs text-red-500">{errors.qualification}</p>
                )}
              </div>

              {/* Specialization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialization/Subject</label>
                <input
                  type="text"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                  placeholder="Enter specialization"
                />
              </div>

              {/* Institution Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name</label>
                <input
                  type="text"
                  name="institution_name"
                  value={formData.institution_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                  placeholder="Enter institution name"
                />
              </div>

              {/* Year of Passing */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                <input
                  type="number"
                  name="passedout"
                  value={formData.passedout}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                  placeholder="Enter year of passing"
                  min="1980"
                  max={new Date().getFullYear()}
                />
              </div>
            </div>

          </div>

          {/* Experience Details */}
          <div className="bg-white rounded-lg shadow-sm p-6 text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Briefcase className="mr-2 text-[#951C25]" size={20} />
              Experience Details
            </h2>

            <div className="space-y-4">
              {/* Prior Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Do you have prior phlebotomy experience? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mt-2">
                  {["Yes", "No"].map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name="phleblot_experience"
                        value={option}
                        checked={formData.phleblot_experience === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.phleblot_experience && <p className="mt-1 text-xs text-red-500">{errors.phleblot_experience}</p>}
              </div>

              {/* Total Experience */}
              {formData.phleblot_experience === "Yes" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Years of Experience</label>
                    <input
                      type="number"
                      name="total_experience"
                      value={formData.total_experience}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                      placeholder="Enter years of experience"
                      min="0"
                      max="50"
                    />
                  </div>

                  {/* Previous Organizations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Previous Organization(s)</label>
                    <textarea
                      name="previous_organization"
                      value={formData.previous_organization}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                      placeholder="List your previous organizations and roles"
                    />
                  </div>
                </>
              )}
            </div>
          </div>


          {/* Other Information */}
          <div className="bg-white rounded-lg shadow-sm p-6 text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Other Information</h2>

            <div className="space-y-4">
              {/* Languages Known */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Languages Known</label>
                <div className="flex flex-wrap gap-4 mt-2">
                  {["English", "Hindi"].map((lang) => (
                    <label key={lang} className="flex items-center">
                      <input
                        type="checkbox"
                        name="languages"
                        value={lang}
                        checked={formData.languages.includes(lang)}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#951C25] focus:ring-[#951C25] rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{lang}</span>
                    </label>
                  ))}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="languages"
                      value="Other"
                      checked={formData.languages.includes("Other")}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#951C25] focus:ring-[#951C25] rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Local Language:</span>
                    <input
                      type="text"
                      name="otherLanguage"
                      value={formData.otherLanguage}
                      onChange={handleInputChange}
                      className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#951C25]/20"
                      placeholder="Specify"
                      disabled={!formData.languages.includes("Other")}
                    />
                  </div>
                </div>
              </div>

              {/* Willing to Travel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Willing to Travel for Sample Collection? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mt-2">
                  {["Yes", "No"].map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name="sample_collection"
                        value={option}
                        checked={formData.sample_collection === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.sample_collection && <p className="mt-1 text-xs text-red-500">{errors.sample_collection}</p>}
              </div>

              {/* Two Wheeler */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Do you have a two-wheeler? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mt-2">
                  {["Yes", "No"].map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name="two_wheeler"
                        value={option}
                        checked={formData.two_wheeler === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.two_wheeler && <p className="mt-1 text-xs text-red-500">{errors.two_wheeler}</p>}
              </div>

              {/* Driving License */}
              {formData.two_wheeler === "Yes" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driving License Number (if applicable)
                  </label>
                  <input
                    type="text"
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                    placeholder="Enter driving license number"
                  />
                </div>
              )}
            </div>
          </div>


          {/* Upload Documents */}
          <div className="bg-white rounded-lg shadow-sm p-6 text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Upload className="mr-2 text-[#951C25]" size={20} />
              Upload Documents
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* File Upload Input Generator */}
              {[
                { label: "Resume (PDF/DOC)", id: "resume", accept: ".pdf,.doc,.docx" },
                { label: "Qualification Certificate", id: "certificate", accept: ".pdf,.jpg,.jpeg,.png" },
                { label: "ID Proof (Aadhaar/PAN)", id: "id_proof", accept: ".pdf,.jpg,.jpeg,.png" },
              ].map(({ label, id, accept }) => (
                <div key={id}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#951C25] transition-colors">
                    <input
                      type="file"
                      accept={accept}
                      onChange={(e) =>
                        setUploadedFiles({ ...uploadedFiles, [id]: e.target.files[0] || null })
                      }
                      className="hidden"
                      id={`${id}-upload`}
                    />
                    <label htmlFor={`${id}-upload`} className="cursor-pointer">
                      {uploadedFiles[id] ? (
                        <div className="flex items-center justify-center text-green-600">
                          <CheckCircle size={20} className="mr-2" />
                          <span className="text-sm">{uploadedFiles[id].name}</span>
                        </div>
                      ) : (
                        <div>
                          <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Click to upload</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>



          {/* Declaration */}
          <div className="bg-white rounded-lg shadow-sm p-6 text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Declaration</h2>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-700">
                I hereby declare that the information provided above is true to the best of my knowledge and belief. I
                understand that any false information may lead to disqualification.
              </p>
            </div>

            <label className="flex items-start">
              <input
                type="checkbox"
                name="declaration"
                checked={formData.declaration}
                onChange={handleInputChange}
                className="w-4 h-4 mt-0.5 text-[#951C25] focus:ring-[#951C25] rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                I agree to the above declaration <span className="text-red-500">*</span>
              </span>
            </label>
            {errors.declaration && <p className="mt-1 text-xs text-red-500">{errors.declaration}</p>}
          </div>

          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#951C25] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#951C25]/90 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting Registration...
                </>
              ) : (
                "Submit Registration"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

